import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './DroneMap.css';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiaWRpLXJvYiIsImEiOiJjbWNuYzB2a3YwMHlhMm9yMDBoYWpqMTZwIn0.stwhSDXvILgSQy6p9rRzgw';

// Default center position (you can adjust these coordinates)
const DEFAULT_CENTER = {
  lat: 40.7128,
  lng: -74.0060,
  heading: 0
};

const DroneMap = ({ position = DEFAULT_CENTER, isConnected, onSwitchToSplitScreen }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [followMode, setFollowMode] = useState(true);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/satellite-streets-v12');
  const [showLayerMenu, setShowLayerMenu] = useState(false);
  const [flightPath, setFlightPath] = useState(() => {
    // Load flight path from localStorage
    try {
      const saved = localStorage.getItem('droneMap_flightPath');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading flight path from localStorage:', error);
      return [];
    }
  });

  // Handle window resize for split screen
  useEffect(() => {
    const handleResize = () => {
      if (map.current) {
        map.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    // Watch for container size changes (e.g., split screen resize)
    let resizeObserver = null;
    let resizeTimeout = null;
    if (mapContainer.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Throttle resize calls to avoid flashing during drag
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
          if (map.current) {
            map.current.resize();
          }
        }, 100); // Wait 100ms after last resize event
      });
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && mapContainer.current) {
        resizeObserver.unobserve(mapContainer.current);
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Resize map when component becomes visible (fixes initial load issue)
  useEffect(() => {
    if (!mapContainer.current) return;

    // Use IntersectionObserver to detect when map becomes visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && map.current) {
            // Map is visible, resize it
            requestAnimationFrame(() => {
              if (map.current) {
                map.current.resize();
              }
            });
          }
        });
      },
      { threshold: 0.1 } // Trigger when at least 10% is visible
    );

    observer.observe(mapContainer.current);

    // Also use requestAnimationFrame for better timing with browser rendering
    const rafResize = () => {
      requestAnimationFrame(() => {
        if (map.current && mapContainer.current) {
          const rect = mapContainer.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            map.current.resize();
          }
        }
      });
    };

    // Multiple resize attempts at different intervals as fallback
    rafResize(); // Immediate
    const timer1 = setTimeout(rafResize, 100);
    const timer2 = setTimeout(rafResize, 300);
    const timer3 = setTimeout(rafResize, 600);
    const timer4 = setTimeout(rafResize, 1000);

    return () => {
      observer.disconnect();
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (map.current) return; // map already initialized

    const currentPosition = position || DEFAULT_CENTER;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [currentPosition.lng, currentPosition.lat],
      zoom: 16,
      pitch: 0, // Top-down view
      bearing: 0
    });

    // Fix scroll wheel zoom to respect mouse position on desktop
    // Mapbox sometimes miscalculates mouse position when containers have CSS transforms/positioning
    if (window.innerWidth >= 1025) {
      // Wait for map to be fully loaded before setting up custom zoom
      map.current.once('load', () => {
        // Disable default scroll zoom
        map.current.scrollZoom.disable();
        
        // Add custom wheel handler that properly calculates mouse position
        const handleWheel = (e) => {
          if (!map.current || !mapContainer.current) return;
          
          // Get the map's canvas element (this is the actual rendered map)
          const canvas = map.current.getCanvasContainer();
          if (!canvas) return;
          
          // Get canvas bounding rect - this gives us the correct coordinate space
          const canvasRect = canvas.getBoundingClientRect();
          
          // Calculate mouse position relative to the canvas (not container)
          // This accounts for any CSS transforms, padding, or positioning offsets
          const x = e.clientX - canvasRect.left;
          const y = e.clientY - canvasRect.top;
          
          // Get current zoom level
          const currentZoom = map.current.getZoom();
          
          // Determine zoom direction and amount (match Mapbox's default behavior)
          const zoomSpeed = 0.1;
          const zoomDelta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
          const newZoom = Math.max(0, Math.min(22, currentZoom + zoomDelta));
          
          // Convert canvas pixel coordinates to geographic coordinates
          // unproject expects pixel coordinates relative to the map canvas
          const point = map.current.unproject([x, y]);
          
          // Zoom to the point under the mouse cursor
          map.current.zoomTo(newZoom, {
            around: point,
            duration: 0 // Instant zoom for better responsiveness
          });
          
          // Prevent default scroll behavior
          e.preventDefault();
          e.stopPropagation();
        };
        
        // Add wheel event listener directly to map container
        mapContainer.current.addEventListener('wheel', handleWheel, { passive: false });
        
        // Store handler and container reference for cleanup
        map.current._customWheelHandler = handleWheel;
        map.current._customWheelContainer = mapContainer.current;
      });
    }

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add 3D terrain
    map.current.on('load', () => {
      map.current.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      });
      map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      
      // Multiple resize attempts with different timings for robustness
      const resizeTimes = [0, 50, 100, 250, 500];
      resizeTimes.forEach(delay => {
        setTimeout(() => {
          if (map.current) {
            map.current.resize();
          }
        }, delay);
      });
    });

    // Create custom marker element for drone position
    const el = document.createElement('div');
    el.className = 'custom-drone-marker';
    el.style.backgroundImage = 'url(/icons/Drone%20Icon%20.svg)';
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundPosition = 'center';
    el.style.display = 'block';
    
    marker.current = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
      rotationAlignment: 'map',
      pitchAlignment: 'map'
    })
      .setLngLat([currentPosition.lng, currentPosition.lat])
      .setRotation(currentPosition.heading || 0) // Default to 0 (north) if no heading
      .addTo(map.current);

    // Add flight path layer
    const addFlightPathLayer = () => {
      if (!map.current.getSource('flight-path')) {
        map.current.addSource('flight-path', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: flightPath
            }
          }
        });
      }

      if (!map.current.getLayer('flight-path')) {
        map.current.addLayer({
          id: 'flight-path',
          type: 'line',
          source: 'flight-path',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#00FF88', // Bright green for better visibility
            'line-width': 5, // Thicker line (increased from 2)
            'line-opacity': 1.0 // Full opacity for clearer visibility
          }
        });
      }
    };

    map.current.on('load', addFlightPathLayer);
    map.current.on('style.load', addFlightPathLayer);

    return () => {
      // Clean up custom wheel handler
      if (map.current && map.current._customWheelHandler && map.current._customWheelContainer) {
        map.current._customWheelContainer.removeEventListener('wheel', map.current._customWheelHandler);
        map.current._customWheelHandler = null;
        map.current._customWheelContainer = null;
      }
      
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update marker position and flight path when position changes
  useEffect(() => {
    if (!map.current || !marker.current || !position) return;

    const newPosition = [position.lng, position.lat];
    
    // Update marker position
    marker.current.setLngLat(newPosition);

    // Update flight path
    setFlightPath(prevPath => {
      const newPath = [...prevPath, newPosition];
      
      // Update flight path on map
      if (map.current.getSource('flight-path')) {
        map.current.getSource('flight-path').setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: newPath
          }
        });
      }

      // Save to localStorage
      try {
        localStorage.setItem('droneMap_flightPath', JSON.stringify(newPath));
      } catch (error) {
        console.error('Error saving flight path to localStorage:', error);
      }

      return newPath;
    });

    // Center map on drone position only if follow mode is enabled
    if (followMode) {
      map.current.easeTo({
        center: newPosition,
        duration: 1000,
        essential: true
      });
    }

    // Update marker rotation based on heading (default to 0/north if null or undefined)
    const heading = position.heading !== null && position.heading !== undefined ? position.heading : 0;
    marker.current.setRotation(heading);
  }, [position, followMode]);

  const toggleFollowMode = () => {
    setFollowMode(!followMode);
  };

  const clearTrail = () => {
    setFlightPath([]);
    try {
      localStorage.removeItem('droneMap_flightPath');
    } catch (error) {
      console.error('Error clearing flight path from localStorage:', error);
    }
    if (map.current && map.current.getSource('flight-path')) {
      map.current.getSource('flight-path').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      });
    }
  };

  const changeMapStyle = (style) => {
    setMapStyle(style);
    if (map.current) {
      map.current.setStyle(style);
    }
    setShowLayerMenu(false);
  };

  const toggleLayerMenu = () => {
    setShowLayerMenu(!showLayerMenu);
  };

  const handleSplitScreen = () => {
    if (onSwitchToSplitScreen) {
      onSwitchToSplitScreen();
    }
  };

  return (
    <div className="drone-map">
      <div className="drone-map__header">
        <div className="header__title">
          <h3>Drone Position</h3>
          <div className="connection-status">
            <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
            <span className="status-text">{isConnected ? 'Live' : 'Offline'}</span>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className={`action-button follow-button ${followMode ? 'active' : ''}`}
            onClick={toggleFollowMode}
            title={followMode ? "Disable Follow Mode" : "Enable Follow Mode"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="3" fill="currentColor"/>
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </button>
          <button 
            className="action-button clear-trail-button" 
            onClick={clearTrail}
            title="Clear Trail"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className="action-button split-screen-button" 
            onClick={handleSplitScreen}
            title="Split Screen View"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="drone-map__content">
        <div ref={mapContainer} className="map-container" />
        
        {/* Map Layer Selector */}
        <div className="map-layer-selector">
          <button
            className="layer-toggle-button"
            onClick={toggleLayerMenu}
            title="Map Layers"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {showLayerMenu && (
            <div className="layer-menu">
              <button
                className={`layer-menu-item ${mapStyle === 'mapbox://styles/mapbox/satellite-streets-v12' ? 'active' : ''}`}
                onClick={() => changeMapStyle('mapbox://styles/mapbox/satellite-streets-v12')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 9h18M9 3v18" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>Satellite</span>
              </button>
              <button
                className={`layer-menu-item ${mapStyle === 'mapbox://styles/mapbox/streets-v12' ? 'active' : ''}`}
                onClick={() => changeMapStyle('mapbox://styles/mapbox/streets-v12')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Streets</span>
              </button>
              <button
                className={`layer-menu-item ${mapStyle === 'mapbox://styles/mapbox/dark-v11' ? 'active' : ''}`}
                onClick={() => changeMapStyle('mapbox://styles/mapbox/dark-v11')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
                </svg>
                <span>Dark</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DroneMap; 